import { injectable } from 'inversify';
import { nanoid } from '@reduxjs/toolkit';

import type {
  FlowEdge,
  FlowNode,
  FlowSchema,
  SelectedNode,
} from '../../types/event-types';

const NEW_STAGE_LABEL = 'Без названия';

@injectable()
export class EventTypesFlowSchemaService {
  private createBranchId() {
    return `Branch_${nanoid()}`;
  }

  createFlowId() {
    return `Flow_${nanoid()}`;
  }

  createTaskId() {
    return `Task_${nanoid()}`;
  }

  createSystemTaskId() {
    return `SystemTask_${nanoid()}`;
  }

  createUserTaskNode(label = NEW_STAGE_LABEL): FlowNode {
    return {
      id: this.createTaskId(),
      type: 'UserTask',
      label,
      allowReturnTo: true,
    };
  }

  createSystemTaskNode(): FlowNode {
    return {
      id: this.createSystemTaskId(),
      type: 'SystemTask',
      label: 'Обработка',
    };
  }

  createFinishEventNode(): FlowNode {
    return {
      id: this.createTaskId(),
      type: 'FinishEvent',
      label: 'Финиш',
      actionType: 'accept',
    };
  }

  private createBranchTaskNode(): FlowNode {
    return {
      id: this.createBranchId(),
      type: 'BranchTask',
      label: NEW_STAGE_LABEL,
      allowReturnTo: true,
    };
  }

  private getGroupNodes(schema: FlowSchema, groupId: string): FlowNode[] {
    return schema.nodes.filter((node) => node.parallelGroupId === groupId);
  }

  private getInsertSourceNodes(
    schema: FlowSchema,
    targetId: string,
  ): FlowNode[] {
    const groupNodes = this.getGroupNodes(schema, targetId);
    if (groupNodes.length) return groupNodes;

    const node = this.getNodeById(schema, targetId);
    return node ? [node] : [];
  }

  getIncomingEdges(schema: FlowSchema, nodeId: string) {
    return schema.edges.filter((edge) => edge.targetId === nodeId);
  }

  getOutgoingEdges(schema: FlowSchema, nodeId: string) {
    return schema.edges.filter((edge) => edge.sourceId === nodeId);
  }

  hasOutgoingReturn(schema: FlowSchema, nodeId: string): boolean {
    return (schema.returns ?? []).some((edge) => edge.sourceId === nodeId);
  }

  isReturnTargetAllowed(
    schema: FlowSchema,
    sourceNodeId: string,
    targetNode: Pick<SelectedNode, 'id' | 'type'>,
  ): boolean {
    const sourceNode = this.getNodeById(schema, sourceNodeId);
    const sourceGroupId = sourceNode?.parallelGroupId;

    if (!sourceNode) {
      return false;
    }

    if (targetNode.type === 'ParallelGroup') {
      if (sourceGroupId && sourceGroupId === targetNode.id) {
        return false;
      }

      return this.canUseAsReturnTarget(schema, targetNode);
    }

    const schemaTargetNode = this.getNodeById(schema, targetNode.id);

    if (!schemaTargetNode) {
      return false;
    }

    if (
      sourceGroupId &&
      schemaTargetNode.parallelGroupId &&
      sourceGroupId === schemaTargetNode.parallelGroupId
    ) {
      return false;
    }

    return this.canUseAsReturnTarget(schema, targetNode);
  }

  canUseAsReturnTarget(
    schema: FlowSchema,
    node: Pick<SelectedNode, 'id' | 'type'>,
  ): boolean {
    if (node.type === 'ParallelGroup') {
      return schema.nodes.some(
        (schemaNode) =>
          schemaNode.parallelGroupId === node.id && schemaNode.allowReturnTo,
      );
    }

    if (node.type === 'StartEvent' || node.type === 'FinishEvent') {
      return false;
    }

    return Boolean(this.getNodeById(schema, node.id)?.allowReturnTo);
  }

  removeReturnConnection(schema: FlowSchema, nodeId: string): FlowSchema {
    const removedReturnTargets = (schema.returns ?? [])
      .filter((edge) => edge.sourceId === nodeId)
      .map((edge) => edge.targetId);
    const newSchema = {
      ...schema,
      returns: (schema.returns ?? []).filter(
        (edge) => edge.sourceId !== nodeId,
      ),
    };
    const shouldCleanupSystemTasks = removedReturnTargets.some((targetId) => {
      const targetNode = this.getNodeById(schema, targetId);

      return targetNode?.type === 'SystemTask';
    });

    return shouldCleanupSystemTasks
      ? this.cleanupSystemTasks(newSchema)
      : newSchema;
  }

  private sortBranchEdges(edges: FlowEdge[]) {
    return [...edges].sort(
      (leftEdge, rightEdge) =>
        Number(!this.isMainBranchEdge(leftEdge)) -
        Number(!this.isMainBranchEdge(rightEdge)),
    );
  }

  private findBranchParent(schema: FlowSchema, nodeId: string) {
    const incoming = this.getIncomingEdges(schema, nodeId);

    return incoming
      .map((edge) => this.getNodeById(schema, edge.sourceId))
      .find((parent) => parent?.type === 'BranchTask');
  }

  private collectDescendants(
    schema: FlowSchema,
    startId: string,
    blockedNodeIds: Set<string>,
  ): Set<string> {
    const collected = new Set<string>();
    const queue = [startId];

    while (queue.length) {
      const currentId = queue.shift();

      if (
        !currentId ||
        blockedNodeIds.has(currentId) ||
        collected.has(currentId)
      ) {
        continue;
      }

      collected.add(currentId);

      this.getOutgoingEdges(schema, currentId).forEach((edge) => {
        if (
          !collected.has(edge.targetId) &&
          !blockedNodeIds.has(edge.targetId)
        ) {
          queue.push(edge.targetId);
        }
      });
    }

    return collected;
  }

  private filterReturnsByNodeIds(
    schema: FlowSchema,
    removedNodeIds: Set<string>,
  ) {
    return (schema.returns ?? []).filter(
      (edge) =>
        !removedNodeIds.has(edge.sourceId) &&
        !removedNodeIds.has(edge.targetId),
    );
  }

  private removeBranchNode(schema: FlowSchema, nodeId: string): FlowSchema {
    const outgoing = this.sortBranchEdges(
      this.getOutgoingEdges(schema, nodeId),
    );
    const mainEdge = outgoing.find(this.isMainBranchEdge);
    const secondaryEdge = outgoing.find((edge) => !this.isMainBranchEdge(edge));

    if (!mainEdge) {
      return schema;
    }

    const blockedNodeIds = new Set<string>([mainEdge.targetId]);
    const secondarySubtree = secondaryEdge
      ? this.collectDescendants(schema, secondaryEdge.targetId, blockedNodeIds)
      : new Set<string>();

    const removedNodeIds = new Set<string>([nodeId]);
    secondarySubtree.forEach((id) => {
      removedNodeIds.add(id);

      const groupId = this.getNodeById(schema, id)?.parallelGroupId;
      if (groupId) {
        this.getGroupNodes(schema, groupId).forEach((groupNode) =>
          removedNodeIds.add(groupNode.id),
        );
      }
    });

    const edges = schema.edges.flatMap((edge) => {
      if (edge.targetId === nodeId) {
        return [
          {
            ...edge,
            id: this.createFlowId(),
            targetId: mainEdge.targetId,
          },
        ];
      }

      if (
        removedNodeIds.has(edge.sourceId) ||
        removedNodeIds.has(edge.targetId)
      ) {
        return [];
      }

      return [edge];
    });

    return {
      nodes: schema.nodes.filter(({ id }) => !removedNodeIds.has(id)),
      edges,
      returns: this.filterReturnsByNodeIds(schema, removedNodeIds),
    };
  }

  removeLinearNode(schema: FlowSchema, nodeId: string): FlowSchema {
    const incoming = this.getIncomingEdges(schema, nodeId);
    const outgoing = this.getOutgoingEdges(schema, nodeId);

    const remainingEdges = schema.edges.filter(
      (edge) => edge.sourceId !== nodeId && edge.targetId !== nodeId,
    );

    const reconnectEdges = incoming.flatMap((inEdge) =>
      outgoing.map((outEdge) => ({
        ...inEdge,
        id: this.createFlowId(),
        targetId: outEdge.targetId,
      })),
    );

    return {
      nodes: schema.nodes.filter(({ id }) => id !== nodeId),
      edges: [...remainingEdges, ...reconnectEdges],
      returns: this.filterReturnsByNodeIds(schema, new Set([nodeId])),
    };
  }

  isMainBranchEdge = (edge: FlowEdge) => edge.condition === 'main';

  getNodeById(schema: FlowSchema, nodeId: string) {
    return schema.nodes.find(({ id }) => id === nodeId);
  }

  canRemoveNode(schema: FlowSchema, nodeId: string) {
    const groupNodes = this.getGroupNodes(schema, nodeId);

    if (groupNodes.length) {
      const hasBranchParent = groupNodes.some(
        (groupNode) => !!this.findBranchParent(schema, groupNode.id),
      );

      if (!hasBranchParent) {
        return true;
      }

      return groupNodes.every(
        (groupNode) => this.getOutgoingEdges(schema, groupNode.id).length > 0,
      );
    }

    const node = this.getNodeById(schema, nodeId);

    if (node?.parallelGroupId) return true;

    if (!node) {
      return false;
    }

    const branchParent = this.findBranchParent(schema, nodeId);

    if (!branchParent) {
      return true;
    }

    return this.getOutgoingEdges(schema, nodeId).length > 0;
  }

  insertNodeAfter(
    schema: FlowSchema,
    selectedNodeId: string,
    newNode: FlowNode = this.createUserTaskNode(),
  ): FlowSchema {
    const sourceNodes = this.getInsertSourceNodes(schema, selectedNodeId);
    if (!sourceNodes.length) return schema;

    const sourceIds = sourceNodes.map(({ id }) => id);
    const sourceIdSet = new Set(sourceIds);
    const index = schema.nodes.findIndex(({ id }) => id === sourceNodes[0].id);

    const newEdges = schema.edges
      .flatMap((edge) => {
        if (sourceIdSet.has(edge.sourceId) && !sourceIdSet.has(edge.targetId)) {
          return [
            {
              ...edge,
              id: this.createFlowId(),
              sourceId: newNode.id,
            },
          ];
        }

        return [edge];
      })
      .concat(
        sourceIds.map((id) => ({
          id: this.createFlowId(),
          sourceId: id,
          targetId: newNode.id,
        })),
      );

    return {
      nodes: [
        ...schema.nodes.slice(0, index + 1),
        newNode,
        ...schema.nodes.slice(index + 1),
      ],
      edges: newEdges,
      returns: schema.returns ?? [],
    };
  }

  insertBranchAfter(schema: FlowSchema, selectedNodeId: string): FlowSchema {
    const sourceNodes = this.getInsertSourceNodes(schema, selectedNodeId);
    if (!sourceNodes.length) return schema;

    const branchNode = this.createBranchTaskNode();
    const mainNode = this.createUserTaskNode(NEW_STAGE_LABEL);
    const secondaryNode = this.createUserTaskNode(NEW_STAGE_LABEL);

    const sourceIds = sourceNodes.map(({ id }) => id);
    const sourceIdSet = new Set(sourceIds);
    const index = schema.nodes.findIndex(({ id }) => id === sourceNodes[0].id);

    const newEdges = schema.edges
      .flatMap((edge) => {
        if (sourceIdSet.has(edge.sourceId) && !sourceIdSet.has(edge.targetId)) {
          return [
            {
              ...edge,
              id: this.createFlowId(),
              sourceId: mainNode.id,
            },
          ];
        }

        return [edge];
      })
      .concat(
        sourceIds.map((id) => ({
          id: this.createFlowId(),
          sourceId: id,
          targetId: branchNode.id,
        })),
        [
          {
            id: this.createFlowId(),
            sourceId: branchNode.id,
            targetId: mainNode.id,
            condition: 'main',
          },
          {
            id: this.createFlowId(),
            sourceId: branchNode.id,
            targetId: secondaryNode.id,
            condition: 'secondary',
          },
        ],
      );

    return {
      nodes: [
        ...schema.nodes.slice(0, index + 1),
        branchNode,
        mainNode,
        secondaryNode,
        ...schema.nodes.slice(index + 1),
      ],
      edges: newEdges,
      returns: schema.returns ?? [],
    };
  }

  removeFinishAfterStart(schema: FlowSchema): FlowSchema {
    const finish = schema.nodes.find(({ type }) => type === 'FinishEvent');

    if (!finish) return schema;

    const incoming = this.getIncomingEdges(schema, finish.id);
    const isFinishAfterStart = incoming.some(({ sourceId }) => {
      const source = this.getNodeById(schema, sourceId);

      return source?.type === 'StartEvent';
    });

    if (!isFinishAfterStart) return schema;

    return this.removeLinearNode(schema, finish.id);
  }

  removeNode(schema: FlowSchema, nodeId: string): FlowSchema {
    const node = this.getNodeById(schema, nodeId);

    if (!node) {
      return schema;
    }

    if (node.type === 'BranchTask') {
      return this.removeFinishAfterStart(
        this.insertSystemTasks(
          this.cleanupSystemTasks(this.removeBranchNode(schema, nodeId)),
        ),
      );
    }

    const result = this.removeLinearNode(schema, nodeId);

    const normalizedFlow = node.parallelGroupId
      ? this.insertSystemTasks(this.cleanupSystemTasks(result))
      : this.insertSystemTasks(result);

    return this.removeFinishAfterStart(normalizedFlow);
  }

  cleanupSystemTasks(schema: FlowSchema): FlowSchema {
    let currentSchema = schema;
    const systemTasks = currentSchema.nodes.filter(
      (node) => node.type === 'SystemTask',
    );

    for (const systemTask of systemTasks) {
      if (this.shouldRemoveSystemTask(currentSchema, systemTask.id)) {
        currentSchema = this.removeLinearNode(currentSchema, systemTask.id);
      }
    }

    return currentSchema;
  }

  shouldRemoveSystemTask(schema: FlowSchema, systemTaskId: string): boolean {
    const incoming = this.getIncomingEdges(schema, systemTaskId);
    const outgoing = this.getOutgoingEdges(schema, systemTaskId);
    const activeReturnEdges = (schema.returns ?? []).filter(
      (edge) => edge.targetId === systemTaskId,
    );

    if (outgoing.length === 0 || incoming.length === 0) {
      return true;
    }

    const incomingSources = incoming
      .map((edge) => this.getNodeById(schema, edge.sourceId))
      .filter((node) => Boolean(node));
    const outgoingTargets = outgoing
      .map((edge) => this.getNodeById(schema, edge.targetId))
      .filter((node) => Boolean(node));
    const hasParallelConnection =
      incomingSources.length > 0 &&
      outgoingTargets.length > 0 &&
      incomingSources.every((node) => Boolean(node?.parallelGroupId)) &&
      outgoingTargets.every((node) => Boolean(node?.parallelGroupId));

    if (hasParallelConnection) {
      return false;
    }

    return activeReturnEdges.length === 0;
  }

  insertSystemTasks(schema: FlowSchema): FlowSchema {
    const crossGroupEdges = schema.edges.filter((edge) => {
      const source = this.getNodeById(schema, edge.sourceId);
      const target = this.getNodeById(schema, edge.targetId);

      if (!source || !target) return false;
      if (source.type === 'SystemTask' || target.type === 'SystemTask')
        return false;
      if (!source.parallelGroupId || !target.parallelGroupId) return false;

      return source.parallelGroupId !== target.parallelGroupId;
    });

    let result = schema;
    for (const edge of crossGroupEdges) {
      result = this.insertSystemTaskNode(result, edge.sourceId, edge.targetId);
    }

    return result;
  }

  insertSystemTaskNode(
    schema: FlowSchema,
    sourceId: string,
    targetId: string,
  ): FlowSchema {
    const source = this.getNodeById(schema, sourceId);
    const target = this.getNodeById(schema, targetId);

    if (!source?.parallelGroupId || !target?.parallelGroupId) return schema;

    const sourceGroupIds = new Set(
      this.getGroupNodes(schema, source.parallelGroupId).map(({ id }) => id),
    );
    const targetGroupIds = new Set(
      this.getGroupNodes(schema, target.parallelGroupId).map(({ id }) => id),
    );

    const crossEdges = schema.edges.filter(
      (edge) =>
        sourceGroupIds.has(edge.sourceId) && targetGroupIds.has(edge.targetId),
    );

    if (!crossEdges.length) return schema;

    const sourceIds = [...new Set(crossEdges.map((edge) => edge.sourceId))];
    const targetIds = [...new Set(crossEdges.map((edge) => edge.targetId))];
    const crossEdgeIds = new Set(crossEdges.map(({ id }) => id));

    const systemNode = this.createSystemTaskNode();

    return {
      nodes: [...schema.nodes, systemNode],
      edges: [
        ...schema.edges.filter((edge) => !crossEdgeIds.has(edge.id)),
        ...sourceIds.map((id) => ({
          id: this.createFlowId(),
          sourceId: id,
          targetId: systemNode.id,
        })),
        ...targetIds.map((id) => ({
          id: this.createFlowId(),
          sourceId: systemNode.id,
          targetId: id,
        })),
      ],
      returns: schema.returns ?? [],
    };
  }
}
